import { useEffect } from 'react';
import { Stack, Group, Card, Title, Text, Button, TextInput, Textarea, ActionIcon, Center, Badge } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconArrowLeft, IconClipboardList, IconTrash, IconDeviceFloppy, IconAlertTriangle, IconInfoCircle, IconPencil, IconPaperclip, IconX } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { confirmDelete } from '../lib/confirm';
import { formatDate } from '../lib/format';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';

interface HomeworkDetailData {
  id: string; title: string; description?: string | null; dueDate?: string | null; fileUrl?: string | null;
  subject?: { id: string; name: string } | null;
  class?: { id: string; name: string } | null;
  teacher?: { id: string; fullName: string } | null;
  /** T6: serverda Toshkent kalendari bo'yicha hisoblangan "muddati o'tgan" belgisi */
  isOverdue?: boolean;
}

const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** Uy vazifa detali — ma'lumot + tahrirlash + o'chirish (bir sahifada). */
export default function HomeworkDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: homework, isLoading } = useQuery({
    queryKey: ['homework', id],
    queryFn: async () => (await api.get<HomeworkDetailData>(`/homework/${id}`)).data,
    enabled: !!id,
  });

  const form = useForm({
    initialValues: { title: '', description: '', dueDate: null as Date | null, fileUrl: '' },
    validate: { title: (v) => (!v ? t('homeworkPage.titleRequired') : null) },
  });
  useEffect(() => {
    if (homework) form.setValues({
      title: homework.title,
      description: homework.description ?? '',
      dueDate: homework.dueDate ? new Date(homework.dueDate) : null,
      fileUrl: homework.fileUrl ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homework?.id]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['homework', id] });
    qc.invalidateQueries({ queryKey: ['homework'] });
  };

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) => api.patch(`/homework/${id}`, { ...v, dueDate: v.dueDate?.toISOString() ?? null }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('common.save') }); invalidate(); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });
  /* Mobil ilova biriktirgan fayl — bucket KALITI (`t~<maktab>/...`), havola emas: matn maydonida
     ko'rsatilmaydi, server bergan qisqa muddatli imzolangan havola bilan ochiladi (token URL'ga
     tushmaydi). Oyna bosish paytida SINXRON ochiladi — `await` dan keyin ochilsa brauzer bloklaydi. */
  const fileKey = form.values.fileUrl && !/^https?:\/\//i.test(form.values.fileUrl) ? form.values.fileUrl : null;
  const openStoredFile = async (key: string) => {
    const w = window.open('', '_blank');
    try {
      const { data } = await api.get<{ url: string }>(`/uploads/link/${key}`);
      if (w) w.location.href = data.url; else window.location.href = data.url;
    } catch (e) {
      w?.close();
      notifications.show({ color: 'red', message: apiErr(e, t('common.error')) });
    }
  };

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/homework/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('homeworkDetail.deleted') }); invalidate(); navigate('/homework'); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  if (isLoading) return <BlockLoader variant="card" />;
  if (!homework) return <Center h={300}><Text c="dimmed">{t('homeworkDetail.notFound')}</Text></Center>;

  return (
    <Stack maw={640}>
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/homework')}><IconArrowLeft size={18} /></ActionIcon>
        <Group gap="xs"><IconClipboardList size={22} color="var(--mantine-color-brand-6)" /><Title order={4}>{homework.title}</Title></Group>
      </Group>

      {/* Ma'lumot (read-only) */}
      <SectionCard title={t('homeworkDetail.info')} icon={IconInfoCircle}>
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap"><Text size="sm" c="dimmed">{t('homeworkPage.subject')}</Text><Text size="sm" fw={500}>{homework.subject?.name ?? '—'}</Text></Group>
          <Group justify="space-between" wrap="nowrap"><Text size="sm" c="dimmed">{t('homeworkPage.class')}</Text><Text size="sm" fw={500}>{homework.class?.name ?? '—'}</Text></Group>
          {/* T6: "kim bergani" va "muddat" bir joyda — vazifa kartochkasidan chiqmasdan ko'rinsin */}
          <Group justify="space-between" wrap="nowrap"><Text size="sm" c="dimmed">{t('homeworkPage.teacher')}</Text><Text size="sm" fw={500}>{homework.teacher?.fullName ?? '—'}</Text></Group>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">{t('homeworkPage.due')}</Text>
            {homework.dueDate ? (
              <Badge color={homework.isOverdue ? 'red' : 'orange'} variant="light" leftSection={homework.isOverdue ? <IconAlertTriangle size={12} /> : undefined}>
                {formatDate(homework.dueDate)}
              </Badge>
            ) : (
              <Text size="sm" c="dimmed">{t('homeworkPage.noDue')}</Text>
            )}
          </Group>
          {homework.isOverdue && <Text size="xs" c="red">{t('homeworkPage.overdueHint')}</Text>}
        </Stack>
      </SectionCard>

      {/* Tahrirlash */}
      <SectionCard title={t('homeworkDetail.edit')} icon={IconPencil}>
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label={t('homeworkPage.titleField')} required {...form.getInputProps('title')} />
            <Textarea label={t('homeworkPage.description')} autosize minRows={3} {...form.getInputProps('description')} />
            <DateInput label={t('homeworkPage.dueDate')} valueFormat="DD.MM.YYYY" clearable {...form.getInputProps('dueDate')} />
            {fileKey ? (
              <Group gap="xs" wrap="nowrap">
                <IconPaperclip size={16} />
                <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                  {t('homeworkDetail.attachedFile')}: {fileKey.split('/').pop()?.replace(/^[0-9a-f-]{36}-/i, '')}
                </Text>
                <Button size="xs" variant="light" onClick={() => void openStoredFile(fileKey)}>{t('homeworkDetail.openFile')}</Button>
                <ActionIcon variant="subtle" color="gray" aria-label={t('homeworkDetail.removeFile')} onClick={() => form.setFieldValue('fileUrl', '')}>
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            ) : (
              <TextInput label={t('homeworkPage.fileUrl')} placeholder="https://..." {...form.getInputProps('fileUrl')} />
            )}
            <Button type="submit" leftSection={<IconDeviceFloppy size={16} />} loading={saveMut.isPending}>{t('common.save')}</Button>
          </Stack>
        </form>
      </SectionCard>

      {/* O'chirish */}
      <Card withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">{t('homeworkDetail.deleteHint')}</Text>
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => confirmDelete(homework.title, () => deleteMut.mutate())} loading={deleteMut.isPending}>
            {t('common.delete')}
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
